// @ts-ignore
import { Schema } from 'tinybase';
import { EXCHANGES } from '@/lib/types/generalTypes';

export interface ConversionSchema {
    conversions: {
        from: { type: 'string' };
        to: { type: 'string' };
        amount: { type: 'number' };
        converted: { type: 'number' };
        exchange: { type: 'string' };
    };
}

export interface ConversionRow {
    from: string;
    to: string;
    amount: number;
    converted: number;
    exchange: EXCHANGES | string;
}

export interface BalancesRow {
    accountId: string;
    symbol: string;
    value: number;
    exchange: EXCHANGES | string;
    wallet: string;
    digits?: number;
    conversionId: string;
    storageId?: string | number;
}

export interface PositionRow {
    accountId: any;
    exchangeId: EXCHANGES;
    liquidationPrice: number | string;
    symbol: string;
    amount: number;
    entryPrice: number;
    pnl: number;
    direction: string;
    side: string;
    pnlCurrency?: string;
    stopLoss?: number;
    isUsdt?: boolean;
    digits: number;
    symbolId?: number | null;
    contractSize?: number;
    // coinm/usdm - BINANCE, inverse/linear - PHEMEX
    contractType?: 'linear' | 'inverse' | 'futures-usdm' | 'futures-coinm' | null;
    posId: string;
    currency?: string;
    coin?: string;
    conversionId: string;
    created?: string | null;
}

export interface QuoteRow {
    id: number;
    symbol: string;
    ask: number;
    bid: number;
    exchangeType: string;
}

export interface BalancesSchema {
    balances: {
        symbol: { type: string };
        value: { type: number };
        accountId: { type: string };
        exchange: { type: string };
        wallet: { type: string };
        digits: { type: number };
        conversionId: { type: string };
    };
}

export interface DerivedBalancesSchema {
    derivedBalances: {
        accountId: { type: string };
        usdValue: { type: number };
        btcValue: { type: number };
        exchange: { type: string };
    };
}

export interface UniqueConversionsNoExchangeSchema {
    uniqueConversionsNoExchange: {
        from: { type: string };
        converted: { type: number };
    };
}

export interface PositionsSchema {
    positions: {
        lastUpdated: { type: string };
        accountId: { type: string };
        exchangeId: { type: string };
        liquidationPrice: { type: number };
        symbol: { type: string };
        amount: { type: number };
        entryPrice: { type: number };
        pnl: { type: number };
        direction: { type: string };
        side: { type: string };
        pnlCurrency: { type: string };
        stopLoss: { type: number };
        isUsdt: { type: boolean };
        digits: { type: number };
        symbolId: { type: number };
        contractSize: { type: number };
        contractType: { type: string };
        posId: { type: string };
        conversionId: { type: string };
        currency: { type: string };
        coin: { type: string };
        created: { type: string };
    };
}

export interface PositionsWithUpnlSchema {
    positionsWithUpnl: {
        accountId: { type: string };
        exchangeId: { type: string };
        liquidationPrice: { type: number };
        symbol: { type: string };
        amount: { type: number };
        entryPrice: { type: number };
        pnl: { type: number };
        direction: { type: string };
        pnlCurrency: { type: string };
        stopLoss: { type: number };
        isUsdt: { type: boolean };
        digits: { type: string };
        symbolId: { type: string };
        contractSize: { type: string };
        contractType: { type: string };
        posId: { type: string };
        conversionId: { type: string };
        currency: { type: string };
        coin: { type: string };
        side: { type: string };
        upnl: { type: number };
        accountName: { type: string };
        pageLink: { type: string };
        price: { type: number };
        created: { type: string };
    };
}

export interface QuotesSchema {
    quotes: {
        id: { type: number };
        symbol: { type: string };
        ask: { type: number };
        bid: { type: number };
        exchangeType: { type: string };
    };
}

export interface BuildersFeeSchema {
    buildersFee: {
        address: { type: string };
        '1h': { type: number };
        '24h': { type: number };
        '7d': { type: number };
        '30d': { type: number };
        all: { type: number };
        timestamp: { type: string };
    };
}

export const conversionSchema: Schema<ConversionSchema> = {
    conversions: {
        from: { type: 'string' },
        to: { type: 'string' },
        amount: { type: 'number' },
        converted: { type: 'number' },
        exchange: { type: 'string' },
    },
};

export const balancesSchema: Schema<BalancesSchema> = {
    balances: {
        symbol: { type: 'string' },
        value: { type: 'number' },
        accountId: { type: 'string' },
        exchange: { type: 'string' },
        wallet: { type: 'string' },
        digits: { type: 'number' },
        conversionId: { type: 'string' },
    },
};

export const derivedBalancesSchema: Schema<DerivedBalancesSchema> = {
    derivedBalances: {
        accountId: { type: 'string' },
        usdValue: { type: 'number' },
        btcValue: { type: 'number' },
        exchange: { type: 'string' },
    },
};

export const uniqueConversionsNoExchangeSchema: Schema<UniqueConversionsNoExchangeSchema> = {
    uniqueConversionsNoExchange: {
        from: { type: 'string' },
        converted: { type: 'number' },
    },
};

export const positionsSchema: Schema<PositionsSchema> = {
    positions: {
        accountId: { type: 'string' },
        exchangeId: { type: 'string' },
        liquidationPrice: { type: 'number' },
        symbol: { type: 'string' },
        amount: { type: 'number' },
        entryPrice: { type: 'number' },
        pnl: { type: 'number' },
        direction: { type: 'string' },
        pnlCurrency: { type: 'string' },
        stopLoss: { type: 'number' },
        isUsdt: { type: 'boolean' },
        digits: { type: 'number' },
        symbolId: { type: 'number' },
        contractSize: { type: 'number' },
        contractType: { type: 'string' },
        posId: { type: 'string' },
        conversionId: { type: 'string' },
        currency: { type: 'string' },
        coin: { type: 'string' },
        side: { type: 'string' },
        created: { type: 'string' },
    },
};

export const positionsWithUpnlSchema: Schema<PositionsWithUpnlSchema> = {
    positionsWithUpnl: {
        accountId: { type: 'string' },
        exchangeId: { type: 'string' },
        liquidationPrice: { type: 'number' },
        symbol: { type: 'string' },
        amount: { type: 'number' },
        entryPrice: { type: 'number' },
        pnl: { type: 'number' },
        pnlCurrency: { type: 'string' },
        isUsdt: { type: 'boolean' },
        digits: { type: 'number' },
        posId: { type: 'string' },
        currency: { type: 'string' },
        coin: { type: 'string' },
        side: { type: 'string' },
        upnl: { type: 'number' },
        accountName: { type: 'string' },
        pageLink: { type: 'string' },
        price: { type: 'number' },
        created: { type: 'string' },
    },
};

export const quotesSchema: Schema<QuotesSchema> = {
    quotes: {
        id: { type: 'number' },
        symbol: { type: 'string' },
        ask: { type: 'number' },
        bid: { type: 'number' },
        exchangeType: { type: 'string' },
    },
};

export interface ActivityStreamSchema {
    id: number;
    posId: number;
    traderCategory: string;
    traderStyle: string;
    event: string;
    direction: string;
    asset: string;
    timestamp: string;
    created: string;
}

export const activityStreamSchema: Schema<ActivityStreamSchema> = {
    activityStream: {
        id: { type: 'number' },
        posId: { type: 'number' },
        traderCategory: { type: 'string' },
        traderStyle: { type: 'string' },
        event: { type: 'string' },
        direction: { type: 'string' },
        asset: { type: 'string' },
        timestamp: { type: 'string' },
        created: { type: 'string' },
    },
};

export interface ActivityStreamRow {
    id: number;
    posId: number;
    traderCategoryId: number;
    traderStyleId: number;
    event: string;
    direction: string;
    asset: string;
    timestamp: string;
    created: string;
}

export interface BuildersFeeRow {
    address: string;
    '1h': number;
    '24h': number;
    '7d': number;
    '30d': number;
    all: number;
    timestamp: string;
}

export const buildersFeeSchema: Schema<BuildersFeeSchema> = {
    buildersFee: {
        address: { type: 'string' },
        '1h': { type: 'number' },
        '24h': { type: 'number' },
        '7d': { type: 'number' },
        '30d': { type: 'number' },
        all: { type: 'number' },
        timestamp: { type: 'string' },
    },
};
